import React, { useEffect, useRef } from "react";
import { getEventColor } from "./ufaApi";

export const FIELD_YARDS = 120;
export const ENDZONE_YARDS = 20;
export const BRICK_YARDS = 20; // Yard line that offense begins at if pull (usually) goes out of bounds before landing.
export const PULL_YARDS = 20; // Yard line that defense pulls from.
export const FIELD_WIDTH = 53.3;

type Point = {
  x: number;
  y: number;
  role: "start" | "end";
  info?: any;
};

type Conn = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  info?: any;
};

interface Props {
  points: Point[];
  connections: Conn[];
  mode: "point" | "heat";
  positionChoice: "start" | "end" | "both";
  showArrows: boolean;
  onHover:
    | ((h: null | { point: Point; left: number; top: number }) => void)
    | null;
  style?: React.CSSProperties;
}

export default function FieldCanvas({
  points,
  connections,
  mode,
  positionChoice,
  showArrows,
  onHover,
  style,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const map = (x: number, y: number) => ({
      x: (x + FIELD_WIDTH / 2) / FIELD_WIDTH,
      y: 1 - y / FIELD_YARDS,
    });

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "#0b6623";
      ctx.fillRect(
        0,
        0,
        canvas.width / devicePixelRatio,
        canvas.height / devicePixelRatio,
      );

      const w = canvas.width / devicePixelRatio;
      const h = canvas.height / devicePixelRatio;
      const cssW = w;
      const cssH = h;
      // debug: show counts
      ctx.fillStyle = "rgba(255,255,255,0.9)";
      ctx.font = `12px sans-serif`;
      const endzoneDepth = ENDZONE_YARDS / FIELD_YARDS;
      ctx.fillStyle = "#0a5a1f";
      ctx.fillRect(0, 0, w, endzoneDepth * h);
      ctx.fillRect(0, (1 - endzoneDepth) * h, w, endzoneDepth * h);

      ctx.strokeStyle = "rgba(255,255,255,0.6)";
      ctx.lineWidth = 1;
      for (let yard = 0; yard <= FIELD_YARDS; yard += 10) {
        if (
          yard === ENDZONE_YARDS / 2 ||
          yard === FIELD_YARDS - ENDZONE_YARDS / 2
        )
          continue;
        const frac = yard / FIELD_YARDS;
        const yPos = frac * h;
        ctx.beginPath();
        ctx.moveTo(0, yPos + 0.5);
        ctx.lineTo(w, yPos + 0.5);
        ctx.stroke();
      }

      ctx.strokeStyle = "rgba(255,255,255,0.9)";
      ctx.lineWidth = 2;
      ctx.strokeRect(0.5, 0.5, w - 1, h - 1);

      if (showArrows && positionChoice === "both" && connections.length > 0) {
        connections.forEach((c) => {
          const eventColor = getEventColor(c.info?.event);
          const p1 = map(c.x1, c.y1);
          const p2 = map(c.x2, c.y2);
          const x1 = p1.x * cssW;
          const y1 = p1.y * cssH;
          const x2 = p2.x * cssW;
          const y2 = p2.y * cssH;

          const ang = Math.atan2(y2 - y1, x2 - x1);
          const ah = Math.max(8, Math.min(cssW, cssH) * 0.02);

          // shorten shaft so it doesn't draw under the arrowhead
          const x2a = x2 - ah * Math.cos(ang);
          const y2a = y2 - ah * Math.sin(ang);

          ctx.save();
          // subtle dark outline for shaft
          ctx.beginPath();
          ctx.strokeStyle = "rgba(0,0,0,0.45)";
          ctx.lineWidth = Math.max(4, Math.min(cssW, cssH) * 0.01);
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2a, y2a);
          ctx.stroke();

          // colored shaft
          ctx.beginPath();
          ctx.strokeStyle = eventColor;
          ctx.lineWidth = Math.max(2, Math.min(cssW, cssH) * 0.006);
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2a, y2a);
          ctx.stroke();

          // arrowhead outline (darker) then fill with team color
          const leftX = x2 - ah * Math.cos(ang - Math.PI / 6);
          const leftY = y2 - ah * Math.sin(ang - Math.PI / 6);
          const rightX = x2 - ah * Math.cos(ang + Math.PI / 6);
          const rightY = y2 - ah * Math.sin(ang + Math.PI / 6);

          ctx.beginPath();
          ctx.moveTo(x2, y2);
          ctx.lineTo(leftX, leftY);
          ctx.lineTo(rightX, rightY);
          ctx.closePath();
          ctx.lineWidth = Math.max(2, Math.min(cssW, cssH) * 0.008);
          ctx.strokeStyle = "rgba(0,0,0,0.6)";
          ctx.stroke();
          ctx.fillStyle = eventColor;
          ctx.fill();

          ctx.restore();
        });

        ctx.fillStyle = "rgba(255,255,255,0.9)";
        ctx.font = `${Math.max(10, Math.min(cssW, cssH) * 0.035)}px sans-serif`;
      }

      // draw points
      if (points && points.length > 0) {
        points.forEach((p) => {
          const eventColor =
            getEventColor(p.info?.event) || "rgba(255,255,255,0.9)";
          const mp = map(p.x, p.y);
          const x = mp.x * cssW;
          const y = mp.y * cssH;
          const r = Math.max(3, Math.min(cssW, cssH) * 0.012);

          // outline for contrast
          ctx.beginPath();
          ctx.arc(x, y, r + 1.2, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(0,0,0,0.45)";
          ctx.fill();

          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.fillStyle = eventColor;
          ctx.fill();
        });
      }
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.floor(rect.width * devicePixelRatio);
      canvas.height = Math.floor(rect.height * devicePixelRatio);
      ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
      draw();
    };

    const resizeHandler = () => requestAnimationFrame(resize);
    resizeHandler();
    window.addEventListener("resize", resizeHandler);
    return () => {
      window.removeEventListener("resize", resizeHandler as EventListener);
    };
  }, [points, mode, connections, showArrows, positionChoice, onHover]);

  const wrapperStyle: React.CSSProperties = {
    position: "relative",
    width: "100%",
    height: "100%",
    ...(style || {}),
  };
  return (
    <div style={wrapperStyle}>
      <canvas
        ref={canvasRef}
        style={{ width: "100%", height: "100%", display: "block" }}
      />
    </div>
  );
}
